import { CreationSource, IMAGE_MIME_TYPES } from '@picsart/cloud-storage/core/enums/files';
import type { UploadFileMetadataProps } from '@picsart/cloud-storage/core/types/api';
import { CrudService } from '@picsart/cloud-storage/services/crud';

import { APP_API_URL, MINIAPP_PACKAGE_ID, ROOT_FOLDER_NAME, ROOT_FOLDER_NAMES } from '@/constants';

import { getContext, isContextAvailable } from '../miniappContext';

// ============================================================================
// SDK PLUMBING — auth headers + singleton CrudService instance
// ============================================================================

/**
 * Get auth headers from the miniapps SDK by making a dummy request
 * and extracting the headers it adds.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const sdkFetch = getContext().utils.api.fetch;
  const headers: Record<string, string> = {};

  // Intercept a single SDK fetch call to extract auth headers
  const originalFetch = globalThis.fetch;
  globalThis.fetch = async (_input: RequestInfo | URL, init?: RequestInit) => {
    // Capture the auth headers the SDK adds
    if (init?.headers) {
      const h = init.headers as Record<string, string>;
      for (const [key, value] of Object.entries(h)) {
        headers[key] = value;
      }
    }
    // Abort — we just needed the headers
    return new Response(null, { status: 200 });
  };

  try {
    await (sdkFetch as typeof globalThis.fetch)('https://api.picsart.com/ping', {});
  } catch {
    // ignore
  } finally {
    globalThis.fetch = originalFetch;
  }

  return headers;
}

let crudServiceInstance: CrudService | null = null;
let authHeadersPromise: Promise<Record<string, string>> | null = null;

/** Get a CrudService initialized with auth headers from the miniapps SDK. */
export async function getCrudService(): Promise<CrudService> {
  if (!authHeadersPromise) {
    authHeadersPromise = getAuthHeaders();
  }
  const headers = await authHeadersPromise;

  if (!crudServiceInstance) {
    crudServiceInstance = new CrudService({ headers });
  }
  return crudServiceInstance;
}

// ============================================================================
// MY LOOKS FOLDER — singleton lookup + creation
// ============================================================================

// Singleton promise — concurrent callers share one in-flight request, no duplicate API calls
let folderUidPromise: Promise<string | null> | null = null;

// Root folder name resolved this session: legacy "My Looks" when an existing
// folder is reused, otherwise the current ROOT_FOLDER_NAME. Used to build the
// generation webhook's lookFolder path so outputs land in the SAME root folder
// as the character metadata (avoids splitting a legacy user's Drive data).
let resolvedRootFolderName: string = ROOT_FOLDER_NAME;

export function clearLibraryDriveCache(): void {
  folderUidPromise = null;
  authHeadersPromise = null;
  crudServiceInstance = null;
  resolvedRootFolderName = ROOT_FOLDER_NAME;
}

export function getMyLooksFolderUid(): Promise<string | null> {
  if (!folderUidPromise) {
    folderUidPromise = (async () => {
      try {
        // Wait for getCrudService so the auth-header capture has restored
        // globalThis.fetch before we issue this direct ctx.fetch call.
        await getCrudService();
        const ctx = getContext();
        const url = new URL(`${APP_API_URL}/cloud-storage/v1/me/files`);
        url.searchParams.set('fileTypes', 'FOLDER');
        const response = await ctx.utils.api.fetch(url.toString());
        if (!response.ok) {
          folderUidPromise = null;
          return null;
        }
        const data = await response.json();
        const folders: Array<{ uid: string; name: string }> = data.response ?? [];
        // Match an accepted root-folder name, honoring ROOT_FOLDER_NAMES
        // priority (new "AI Stylist" before legacy "My Looks") rather than the
        // Drive API's response order. Reusing the legacy folder keeps a user's
        // existing looks rather than orphaning them in a renamed folder.
        const matched = ROOT_FOLDER_NAMES.map(name => folders.find(f => f.name === name)).find(Boolean);
        if (matched) resolvedRootFolderName = matched.name;
        const uid = matched?.uid ?? null;
        if (!uid) folderUidPromise = null;
        return uid;
      } catch (e) {
        console.warn('[driveFolderService] folder resolve failed:', e);
        folderUidPromise = null;
        return null;
      }
    })();
  }
  return folderUidPromise;
}

/**
 * Get the root folder UID, creating it if it doesn't exist. Reuses a legacy
 * "My Looks" folder when present (via getMyLooksFolderUid); otherwise creates
 * the folder under the current name ("AI Stylist").
 */
export async function getOrCreateMyLooksFolderUid(): Promise<string | null> {
  const existing = await getMyLooksFolderUid();
  if (existing) return existing;

  // Folder doesn't exist — create it
  try {
    const ctx = getContext();
    const response = await ctx.utils.api.fetch(`${APP_API_URL}/cloud-storage/v1/me/folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: ROOT_FOLDER_NAME }),
    });

    if (!response.ok) {
      console.error('[DriveFolderService] Failed to create My Looks folder:', response.status);
      return null;
    }

    const data = await response.json();
    const uid = data.response?.uid ?? null;
    if (uid) {
      // Clear cache so next call picks up the new folder
      folderUidPromise = null;
      notifyMyLooksFolderCreated();
    }
    return uid;
  } catch (err) {
    console.error('[DriveFolderService] Error creating My Looks folder:', err);
    return null;
  }
}

/**
 * Resolve the root folder name in use this session — the legacy "My Looks" when
 * an existing folder is reused, otherwise the current ROOT_FOLDER_NAME. Runs the
 * folder lookup first so callers building the lookFolder path get the real root
 * (and don't route generation output to a fresh "AI Stylist" folder for users
 * whose data still lives under "My Looks").
 */
export async function getRootFolderName(): Promise<string> {
  await getMyLooksFolderUid();
  return resolvedRootFolderName;
}

// ============================================================================
// MY LOOKS FOLDER — created-callbacks (notified when the folder appears)
// ============================================================================

// Callbacks notified when "My Looks" folder becomes available (created by addImageToDrive).
// Used by other services (e.g. wardrobeItemsStorageService) to flush deferred saves.
const myLooksFolderCreatedCallbacks: Array<() => void> = [];

export function onMyLooksFolderCreated(cb: () => void): void {
  myLooksFolderCreatedCallbacks.push(cb);
}

function notifyMyLooksFolderCreated(): void {
  myLooksFolderCreatedCallbacks.forEach(cb => {
    try {
      cb();
    } catch {
      /* ignore */
    }
  });
}

// ============================================================================
// CHARACTER SUBFOLDERS — per-character subfolder under "My Looks"
// ============================================================================

// In-flight character-persistence promises keyed by character id. Lets concurrent
// callers (upload-time saveCharacterToDrive vs. generate-time fallback) share one
// save instead of racing and creating duplicate subfolders.
const characterPersistPromises = new Map<string, Promise<{ uid: string; name: string } | null>>();

export function registerCharacterPersist(characterId: string, promise: Promise<{ uid: string; name: string } | null>): void {
  characterPersistPromises.set(characterId, promise);
  void promise.finally(() => {
    if (characterPersistPromises.get(characterId) === promise) {
      characterPersistPromises.delete(characterId);
    }
  });
}

export function awaitCharacterPersist(characterId: string): Promise<{ uid: string; name: string } | null> | null {
  return characterPersistPromises.get(characterId) ?? null;
}

export interface EnsureCharacterSubfolderInput {
  characterId: string;
  characterImageUrl: string | null;
  /** Caller-provided lookup of the character's current subfolder from store. */
  getCurrent: () => { uid: string; name: string } | null;
  /** Called when a new subfolder is created so the caller can persist it. */
  setSubfolder: (uid: string, name: string) => void;
}

/**
 * Resolve the Drive subfolder for a character, creating it if missing. Used by
 * all generation entry points (desktop PreviewPanel + mobile useGeneration) so
 * the look image always lands in the same subfolder as the character-reference
 * and the per-character JSON. Dedupes against any in-flight upload-time persist
 * so a concurrent Generate doesn't create a parallel folder.
 */
export async function ensureCharacterSubfolder(input: EnsureCharacterSubfolderInput): Promise<{ uid: string; name: string } | null> {
  let resolved = input.getCurrent();
  if (resolved) return resolved;

  const inflight = awaitCharacterPersist(input.characterId);
  if (inflight) {
    await inflight;
    resolved = input.getCurrent();
    if (resolved) return resolved;
  }

  if (!isContextAvailable()) return null;

  const createPromise = (async (): Promise<{ uid: string; name: string } | null> => {
    const name = `Look-${input.characterId.slice(0, 8)}-${Date.now()}`;
    try {
      const uid = await createLookSubfolder(name);
      if (!uid) return null;
      if (input.characterImageUrl) {
        await saveImageToFolder(input.characterImageUrl, uid);
      }
      input.setSubfolder(uid, name);
      return { uid, name };
    } catch (err) {
      console.error('[DriveFolderService] ensureCharacterSubfolder error:', err);
      return null;
    }
  })();

  registerCharacterPersist(input.characterId, createPromise);
  return await createPromise;
}

// ============================================================================
// SUBFOLDER + FILE CRUD — create subfolders, save image/JSON, delete
// ============================================================================

/**
 * Create a subfolder inside "My Looks" and return its ID.
 * If the "My Looks" folder doesn't exist yet, returns null.
 */
export const createLookSubfolder = async (name: string): Promise<string | null> => {
  if (!isContextAvailable()) return null;

  try {
    const parentUid = await getOrCreateMyLooksFolderUid();
    if (!parentUid) {
      console.error('[DriveFolderService] createLookSubfolder: could not get/create My Looks folder');
      return null;
    }

    const crud = await getCrudService();
    const result = await crud.createFolder({ name, parentFolderId: parentUid });

    if (result.status === 'success' && result.response) {
      const folderId = result.response.file.id;
      return folderId;
    }

    return null;
  } catch (err) {
    console.error('[DriveFolderService] createLookSubfolder error:', err);
    return null;
  }
};

/**
 * Register a CDN image into a specific Drive folder (by folder ID).
 * Uses createFile — registers the sourceUrl without re-upload.
 */
export const saveImageToFolder = async (imageUrl: string, folderId: string): Promise<string | null> => {
  if (!isContextAvailable()) return null;

  try {
    const crud = await getCrudService();
    const result = await crud.createFile({
      sourceUrl: imageUrl,
      name: 'character-reference',
      mimeType: IMAGE_MIME_TYPES.JPEG,
      parentFolderId: folderId,
      creationSource: CreationSource.AI_SERVICES,
    });

    if (result.status === 'success' && result.response) {
      const fileId = result.response.file.id;
      return fileId;
    }

    console.error('[DriveFolderService] saveImageToFolder failed:', result);
    return null;
  } catch (err) {
    console.error('[DriveFolderService] saveImageToFolder error:', err);
    return null;
  }
};

/**
 * Upload a JSON file into a Drive folder, marked hidden so it doesn't show
 * in the Drive UI. The SDK's typed `UploadFileMetadataProps` doesn't expose
 * `hidden`, but `uploadFile` spreads `metadata` straight into the multipart
 * Blob — so the backend honors `hidden: true` and `attributes.privateVisibility`.
 */
async function findHiddenFileInFolder(folderId: string, fileName: string): Promise<string | null> {
  try {
    const crud = await getCrudService();
    const result = await crud.getFiles({
      params: { parentFolderUid: folderId, limit: '50', offset: '0' },
      onlyHiddenFiles: true,
    });
    if (result.status !== 'success' || !Array.isArray(result.response)) return null;
    const match = result.response.find(item => item.name === fileName);
    if (!match) return null;
    return (match as { uid?: string; id?: string }).uid ?? (match as { id?: string }).id ?? null;
  } catch (e) {
    console.warn('[driveFolderService] file lookup failed:', e);
    return null;
  }
}

type ExtendedUploadMetadata = Omit<UploadFileMetadataProps, 'attributes'> & {
  hidden?: boolean;
  attributes?: Record<string, string | number | boolean>;
};

const toUploadMetadata = (meta: ExtendedUploadMetadata): UploadFileMetadataProps => meta as unknown as UploadFileMetadataProps;

export const saveJsonToFolder = async (jsonData: string, fileName: string, folderId: string): Promise<string | null> => {
  if (!isContextAvailable()) return null;

  try {
    const crud = await getCrudService();
    const file = new File([jsonData], fileName, { type: 'application/json' });
    const metadata: ExtendedUploadMetadata = {
      name: fileName,
      parentFolderId: folderId,
      hidden: true,
      attributes: { tool: MINIAPP_PACKAGE_ID, privateVisibility: 'HIDDEN' },
    };

    // PATCH-or-POST: update the file in place if it already exists in this folder,
    // otherwise create a new one. Without this, every save would create a duplicate.
    const sdkMetadata = toUploadMetadata(metadata);
    const existingId = await findHiddenFileInFolder(folderId, fileName);
    if (existingId) {
      const updated = await crud.updateFileResourceAndMeta({ fileId: existingId, file, metadata: sdkMetadata });
      if (updated.status === 'success' && updated.response) return updated.response.file.id;
      // Fell through — file may have been deleted server-side; create fresh below.
    }

    const result = await crud.uploadFile({ file, metadata: sdkMetadata });
    if (result.status === 'success' && result.response) return result.response.file.id;
    console.error('[DriveFolderService] saveJsonToFolder failed:', result);
    return null;
  } catch (err) {
    console.error('[DriveFolderService] saveJsonToFolder error:', err);
    return null;
  }
};

/**
 * Delete a character's Drive subfolder (along with everything inside it —
 * the character image, generated images, and the hidden JSON). Best-effort,
 * fire-and-forget: returns false on failure so callers can ignore the result.
 */
export async function deleteCharacterFolderFromDrive(folderUid: string): Promise<boolean> {
  if (!isContextAvailable()) return false;
  try {
    const crud = await getCrudService();
    const result = await crud.deleteFile({ fileId: folderUid });
    return result.status === 'success';
  } catch (err) {
    console.error('[DriveFolderService] deleteCharacterFolderFromDrive error:', err);
    return false;
  }
}

/**
 * Delete a single file from Drive by its uid. Used when a look is removed and
 * its generated image (auto-saved to the character's subfolder by the
 * generation webhook) needs to be cleaned up. Best-effort, fire-and-forget.
 */
export async function deleteFileFromDrive(fileUid: string): Promise<boolean> {
  if (!isContextAvailable()) return false;
  try {
    const crud = await getCrudService();
    const result = await crud.deleteFile({ fileId: fileUid });
    return result.status === 'success';
  } catch (err) {
    console.error('[DriveFolderService] deleteFileFromDrive error:', err);
    return false;
  }
}

// ============================================================================
// ADD-TO-DRIVE WORKFLOW — single-image registration that also creates the
// "My Looks" folder if needed and notifies subscribers.
// ============================================================================

export interface AddToDriveOptions {
  imageUrl: string;
  folderName?: string;
}

export interface AddToDriveResult {
  uid: string;
  success: boolean;
  error?: string;
}

/**
 * Add an image to user's Picsart Drive
 * Saves to a specified folder (default: the root folder, "AI Stylist")
 *
 * @param options.imageUrl - URL of the image to save
 * @param options.folderName - Folder name in Drive (default: "AI Stylist")
 * @returns AddToDriveResult with uid and success status
 */
export const addImageToDrive = async (options: AddToDriveOptions): Promise<AddToDriveResult> => {
  const { imageUrl, folderName = ROOT_FOLDER_NAME } = options;

  if (!isContextAvailable()) {
    return { uid: '', success: false, error: 'Must run inside Picsart Editor' };
  }

  try {
    const context = getContext();
    const requestUrl = `${APP_API_URL}/workflows/add-to-drive/execute`;

    const response = await context.utils.api.fetch(requestUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        params: {
          name: 'photo',
          url: imageUrl,
          content: {
            type: 'STANDALONE',
            resourceType: 'PHOTO',
            sourcePlatform: 'WEB',
          },
          preview: { url: imageUrl },
          folder: {
            name: folderName,
            content: { uid: MINIAPP_PACKAGE_ID },
          },
          attributes: { tool: MINIAPP_PACKAGE_ID },
        },
      }),
    });
    const result = await response.json();

    const uid = result.response?.result?.uid ?? '';

    if (uid) {
      // Folder was just created — clear cache and notify all registered callbacks
      folderUidPromise = null;
      notifyMyLooksFolderCreated();
    }
    return { uid, success: !!uid };
  } catch (error) {
    return {
      uid: '',
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save to Drive',
    };
  }
};
